// AuthContext.tsx
import * as Sentry from "@sentry/react";
import type { Session, User } from "@supabase/supabase-js";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import useSWR, { type SWRConfiguration } from "swr";
import LoadingDots from "@/components/ui/loading-dots";
import { supabaseClient } from "@/lib/supabase";
import { AUTH_CACHE_KEYS } from "@/services/entities/cache-keys";

// Types
interface UserProfile {
  staffId: string | null;
  isAdmin: boolean;
  isTutor: boolean;
  userId: string | null;
  userData: {
    userId: string | null;
    firstName: string | null;
    lastName: string | null;
    familyId: number | null;
  };
  staffData: {
    staffId: string | null;
    isAdmin: boolean;
    isTutor: boolean;
    firstName: string | null;
    lastName: string | null;
  };
}

interface AuthContextValue {
  // Core auth state
  authUser: User | null;
  session: Session | null;
  isLoading: boolean;
  error: Error | null;

  // Profile data
  cognitoSub: string | null;
  staffId: string;
  userId: string;
  familyId: number | null;
  userFirstName: string | null;
  userLastName: string | null;
  isAdmin: boolean;
  isTutor: boolean;
  isStaff: boolean;
  isClientOnly: boolean;
  staffFirstName: string | null;
  staffLastName: string | null;
  dbProfile?: UserProfile | null;

  // Actions
  signOut: () => Promise<void>;
  refreshAuthData: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextValue | null>(null);

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Fetch user profile function
const fetchUserStaff = async (userUuid: string): Promise<UserProfile | null> => {
  try {
    if (!userUuid || userUuid.length !== 36) {
      throw new Error("Invalid Cognito Sub provided");
    }

    const { data, error } = await supabaseClient.rpc("get_user_profile").maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    Sentry.setTags({
      staffId: data.staff_id,
      userId: data.user_id,
    });

    return {
      staffId: data.staff_id ?? null,
      isAdmin: data.is_admin ?? false,
      isTutor: data.is_tutor ?? false,
      userId: data.user_id ?? null,
      userData: {
        userId: data.user_id,
        firstName: data.user_first_name,
        lastName: data.user_last_name,
        familyId: data.family_id,
      },
      staffData: {
        staffId: data.staff_id,
        isAdmin: data.is_admin,
        isTutor: data.is_tutor,
        firstName: data.staff_first_name,
        lastName: data.staff_last_name,
      },
    };
  } catch (error) {
    console.error("Error fetching user staff data:", error);
    return null;
  }
};

// SWR configuration
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 60000, // 1 minute
  focusThrottleInterval: 30000, // 30 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const navigate = useNavigate();

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabaseClient.auth.getSession();

        if (!mounted) return;

        if (error) {
          console.error("Error getting initial session:", error);
          setError(error);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        if (!mounted) return;
        console.error("Error in getInitialSession:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log("Auth state changed:", event, session?.user?.id);

      setSession(session);
      setUser(session?.user ?? null);
      setError(null);

      // Set Sentry user context
      if (session?.user) {
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        });
      } else {
        Sentry.setUser(null);
      }

      if (session?.user?.id) {
        refreshDbProfile();
      }

      // Only set loading to false after initial session is resolved
      if (event !== "INITIAL_SESSION") {
        setIsLoading(false);
      }
      if (event === "INITIAL_SESSION") {
        // handle initial session
      } else if (event === "SIGNED_IN") {
        // handle sign in event
      } else if (event === "SIGNED_OUT") {
        // handle sign out event
      } else if (event === "PASSWORD_RECOVERY") {
        // handle password recovery event
      } else if (event === "TOKEN_REFRESHED") {
        // handle token refreshed event
      } else if (event === "USER_UPDATED") {
        // handle user updated event
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Fetch profile data with SWR
  const {
    data: dbProfile,
    error: dbProfileError,
    isLoading: dbProfileLoading,
    mutate: refreshDbProfile,
  } = useSWR(
    user ? [AUTH_CACHE_KEYS.USER_INFO, user.id] : null,
    () => fetchUserStaff(user?.id!),
    swrConfig,
  );

  useEffect(() => {
    console.log("AuthProvider state:", {
      isLoading,
      dbProfileLoading,
      user: !!user,
      session: !!session,
      error,
    });
  }, [isLoading, dbProfileLoading, user, session, error]);

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      await supabaseClient.auth.signOut();
      refreshDbProfile(null, false);
      Sentry.setUser(null);
      navigate({ to: "/" });
    } catch (error) {
      console.error("Error signing out:", error);
      setError(error instanceof Error ? error : new Error("Sign out failed"));
    } finally {
      setIsLoading(false);
    }
  }, [navigate, refreshDbProfile]);

  // Refresh auth data function
  const refreshAuthData = useCallback(async () => {
    await refreshDbProfile();
  }, [refreshDbProfile]);

  // Compute derived values
  const contextValue: AuthContextValue = {
    // Core auth state
    authUser: user,
    session,
    isLoading: isLoading || dbProfileLoading,
    error: error || dbProfileError,

    // Profile data
    cognitoSub: user?.id ?? null,
    staffId: dbProfile?.staffId || "",
    userId: dbProfile?.userId || "",
    familyId: dbProfile?.userData.familyId ?? null,
    userFirstName: dbProfile?.userData.firstName ?? null,
    userLastName: dbProfile?.userData.lastName ?? null,
    isAdmin: dbProfile?.isAdmin || false,
    isTutor: dbProfile?.isTutor || false,
    isStaff: dbProfile?.staffId ? true : false,
    isClientOnly: !dbProfile?.isAdmin && !dbProfile?.isTutor,
    staffFirstName: dbProfile?.staffData.firstName ?? null,
    staffLastName: dbProfile?.staffData.lastName ?? null,
    dbProfile,

    // Actions
    signOut,
    refreshAuthData,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// Optional: Higher-order component for protecting routes
interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireStaff?: boolean;
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireStaff = false,
  fallbackPath = "/login",
}: ProtectedRouteProps) {
  const { authUser, isAdmin, isStaff, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <div className="text-xl font-medium text-foreground">Loading User</div>
        <LoadingDots />
      </div>
    );
  }

  if (!authUser) {
    return <Navigate to={fallbackPath} />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to={fallbackPath} />;
  }

  if (requireStaff && !isStaff) {
    return <Navigate to={fallbackPath} />;
  }

  return <>{children}</>;
}
